import type { JSX } from "react";
import styled from "styled-components";
import { currentColors } from "../colors";

interface IProps {
    proportion: number;
    label: string;
    className?: string;
}

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    aspect-ratio: 1;
    max-width: 200px;
`;

const PercentageText = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    color: ${currentColors.text};
`;

function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
}

function describeDonutSegment(
    x: number,
    y: number,
    rOuter: number,
    rInner: number,
    startAngle: number,
    endAngle: number,
): string {
    const startOuter = polarToCartesian(x, y, rOuter, startAngle);
    const endOuter = polarToCartesian(x, y, rOuter, endAngle);
    const startInner = polarToCartesian(x, y, rInner, startAngle);
    const endInner = polarToCartesian(x, y, rInner, endAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    const d = [
        "M",
        startOuter.x,
        startOuter.y,
        "A",
        rOuter,
        rOuter,
        0,
        largeArcFlag,
        1,
        endOuter.x,
        endOuter.y,
        "L",
        endInner.x,
        endInner.y,
        "A",
        rInner,
        rInner,
        0,
        largeArcFlag,
        0,
        startInner.x,
        startInner.y,
        "Z",
    ].join(" ");

    return d;
}

function CircularProgress({ proportion, label, className }: IProps): JSX.Element {
    const clampedProportion = Math.max(0, Math.min(1, proportion));

    const totalSegments = 10;
    const viewBoxSize = 100;
    const center = viewBoxSize / 2;
    const rOuter = 48;
    const rInner = 38;
    const gapAngle = 6;
    const segmentAngle = 360 / totalSegments - gapAngle;

    const filledSegments = Math.floor(clampedProportion * totalSegments);
    const percentage = Math.round(clampedProportion * 100);

    const filledColor = currentColors.performance;
    const unfilledColor = currentColors.performanceBackground;

    const segments = Array.from({ length: totalSegments }).map((_, i) => {
        const startAngle = i * (360 / totalSegments);
        const endAngle = startAngle + segmentAngle;
        const pathData = describeDonutSegment(center, center, rOuter, rInner, startAngle, endAngle);
        const isFilled = i < filledSegments;

        return (
            <path
                key={i}
                d={pathData}
                fill={isFilled ? filledColor : unfilledColor}
                stroke={filledColor}
                strokeWidth={0.5}
            />
        );
    });

    return (
        <Container
            role="progressbar"
            aria-label={label}
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={`${percentage}%`}
            tabIndex={0}
            className={className}
        >
            <svg aria-hidden="true" viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}>
                {segments}
            </svg>
            <PercentageText aria-hidden="true">{percentage}%</PercentageText>
        </Container>
    );
}

export default CircularProgress;
